import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { settingsService } from '../../services/settingsService';

const { FiSettings, FiSave, FiMail, FiPhone, FiMapPin, FiClock, FiDollarSign } = FiIcons;

const AdminSettings = () => {
  const [settings, setSettings] = useState(settingsService.getDefaultSettings());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await settingsService.getSettings();
      if (data) {
        setSettings(data);
      }
      if (error) {
        console.error('Error loading settings:', error);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({
      ...settings,
      [field]: value
    });
  };

  const handleHoursChange = (day, type, value) => {
    setSettings({
      ...settings,
      businessHours: {
        ...settings.businessHours,
        [day]: {
          ...settings.businessHours[day],
          [type]: value
        }
      }
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      
      const { data, error } = await settingsService.saveSettings(settings);
      
      if (error) {
        setMessage({ type: 'error', text: 'Failed to save settings' });
        console.error('Save error:', error);
      } else {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
        // Reload the page after a short delay to apply changes
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'An error occurred while saving' });
      console.error('Error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-[22px] font-semibold tracking-[-0.01em] text-adm-text-hi mb-6">Admin Settings</h2>
        
        {message.text && (
          <div className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            {message.text}
          </div>
        )}
        
        {/* Business Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-6 mb-6"
        >
          <h3 className="text-[15px] font-semibold text-adm-text-hi mb-4 flex items-center">
            <SafeIcon icon={FiSettings} className="w-5 h-5 mr-2" />
            Business Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Name
              </label>
              <input
                type="text"
                value={settings.businessName}
                onChange={(e) => handleChange('businessName', e.target.value)}
                className="form-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <div className="relative">
                <SafeIcon icon={FiPhone} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={settings.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <div className="relative">
                <SafeIcon icon={FiMapPin} className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={settings.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
          </div>
        </motion.div>
        
        {/* Business Hours */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-6 mb-6"
        >
          <h3 className="text-[15px] font-semibold text-adm-text-hi mb-4 flex items-center">
            <SafeIcon icon={FiClock} className="w-5 h-5 mr-2" />
            Business Hours
          </h3>
          
          <div className="space-y-3">
            {Object.entries(settings?.businessHours || {}).map(([day, hours]) => (
              <div key={day} className="flex items-center space-x-4">
                <div className="w-32">
                  <span className="text-sm font-medium text-gray-700 capitalize">
                    {day}
                  </span>
                </div>
                <input
                  type="text"
                  value={hours?.open || ''}
                  onChange={(e) => handleHoursChange(day, 'open', e.target.value)}
                  className="form-input text-sm"
                  placeholder="Open"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="text"
                  value={hours?.close || ''}
                  onChange={(e) => handleHoursChange(day, 'close', e.target.value)}
                  className="form-input text-sm"
                  placeholder="Close"
                />
              </div>
            ))}
          </div>
        </motion.div>
        
        {/* Pricing Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-6 mb-6"
        >
          <h3 className="text-[15px] font-semibold text-adm-text-hi mb-4 flex items-center">
            <SafeIcon icon={FiDollarSign} className="w-5 h-5 mr-2" />
            Pricing Settings
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sales Tax Rate (%)
              </label>
              <input
                type="number"
                step="0.01"
                value={settings.salesTaxRate ?? ''}
                onChange={(e) => handleChange('salesTaxRate', parseFloat(e.target.value))}
                className="form-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Documentation Fee ($)
              </label>
              <input
                type="number"
                value={settings.docFee ?? ''}
                onChange={(e) => handleChange('docFee', parseInt(e.target.value))}
                className="form-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Finance Rate (%)
              </label>
              <input
                type="number"
                step="0.1"
                value={settings.defaultFinanceRate ?? ''}
                onChange={(e) => handleChange('defaultFinanceRate', parseFloat(e.target.value))}
                className="form-input"
              />
            </div>
          </div>
        </motion.div>
        
        {/* Notification Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-6 mb-6"
        >
          <h3 className="text-[15px] font-semibold text-adm-text-hi mb-4">
            Notification Settings
          </h3>
          
          <div className="space-y-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.emailNotifications}
                onChange={(e) => handleChange('emailNotifications', e.target.checked)}
                className="form-checkbox h-4 w-4 text-primary-600"
              />
              <span className="ml-2 text-sm text-gray-700">
                Email notifications for new leads
              </span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.smsNotifications}
                onChange={(e) => handleChange('smsNotifications', e.target.checked)}
                className="form-checkbox h-4 w-4 text-primary-600"
              />
              <span className="ml-2 text-sm text-gray-700">
                SMS notifications for urgent matters
              </span>
            </label>
            
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={settings.appointmentReminders}
                onChange={(e) => handleChange('appointmentReminders', e.target.checked)}
                className="form-checkbox h-4 w-4 text-primary-600"
              />
              <span className="ml-2 text-sm text-gray-700">
                Automatic appointment reminders
              </span>
            </label>
          </div>
        </motion.div>
        
        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-end"
        >
          <button
            onClick={handleSave}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-adm-ctl text-[13px] font-medium transition-colors flex items-center"
          >
            <SafeIcon icon={FiSave} className="w-5 h-5 mr-2" />
            Save Settings
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminSettings;
